import { createAuthClient } from "better-auth/react";
import { organizationClient } from "better-auth/client/plugins";
import { apiKeyClient } from "@better-auth/api-key/client";
import { ssoClient } from "@better-auth/sso/client";
import { MOCK_ENABLED, MOCK_ORGS, MOCK_SESSION_KEY, MOCK_USER, mockCanCreateOrg, mockCreateOrg } from "./mock.ts";

const baseURL =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ??
  "https://api.currency-core.com";

export const authClient = createAuthClient({
  baseURL,
  plugins: [organizationClient(), apiKeyClient(), ssoClient()],
});

// ---------------------------------------------------------------------------
// Mock auth (dev only — VITE_MOCK_AUTH=true). Lets you sign in with any input
// and exercise every dashboard page without a backend.
// ---------------------------------------------------------------------------
const setLogin = (on: boolean) => {
  try {
    if (on) window.localStorage.setItem(MOCK_SESSION_KEY, "1");
    else window.localStorage.removeItem(MOCK_SESSION_KEY);
  } catch {
    /* ignore */
  }
};

function mockUseSession() {
  const loggedIn =
    typeof window !== "undefined" &&
    window.localStorage.getItem(MOCK_SESSION_KEY) === "1";
  return {
    data: loggedIn ? { user: MOCK_USER, session: { id: "sess_mock" } } : null,
    isPending: false,
    error: null,
  };
}

const mockSignIn = {
  email: async () => {
    setLogin(true);
    return { data: { user: MOCK_USER }, error: null };
  },
  social: async (opts?: { callbackURL?: string }) => {
    setLogin(true);
    window.location.href = opts?.callbackURL ?? "/dashboard";
    return { data: null, error: null };
  },
  sso: async (opts?: { callbackURL?: string }) => {
    setLogin(true);
    window.location.href = opts?.callbackURL ?? "/dashboard";
    return { data: null, error: null };
  },
};

const mockSignUp = {
  email: async () => {
    setLogin(true);
    return { data: { user: MOCK_USER }, error: null };
  },
};

const mockSendVerificationEmail = async () => ({ data: { status: true }, error: null });
const mockRequestPasswordReset = async () => ({
  data: { status: true, message: "If this email exists in our system, check your email for the reset link" },
  error: null,
});
const mockResetPassword = async () => ({ data: { status: true }, error: null });

const mockSignOut = async () => {
  setLogin(false);
  return { data: null, error: null };
};

const mockOrganization = {
  list: async () => ({ data: MOCK_ORGS, error: null }),
  create: async (opts: { name: string; slug: string }) => {
    if (!mockCanCreateOrg()) {
      return {
        data: null,
        error: {
          message:
            "You need at least one paid organization to create more. Upgrade an org you own to a paid plan, then you can create additional organizations.",
        },
      };
    }
    const org = mockCreateOrg(opts.name, opts.slug);
    return { data: { id: org.id, name: org.name, slug: org.slug }, error: null };
  },
};

const mockUseListOrganizations = () => ({ data: MOCK_ORGS, isPending: false, error: null });
const mockUseActiveOrganization = () => ({ data: MOCK_ORGS[0], isPending: false, error: null });
const mockApiKey = {
  list: async () => ({ data: [], error: null }),
  create: async () => ({ data: null, error: null }),
  delete: async () => ({ data: null, error: null }),
};

// Export the real client surface, or the mock when the dev flag is set.
export const useSession = MOCK_ENABLED
  ? (mockUseSession as unknown as typeof authClient.useSession)
  : authClient.useSession;
export const signIn = MOCK_ENABLED
  ? (mockSignIn as unknown as typeof authClient.signIn)
  : authClient.signIn;
export const signUp = MOCK_ENABLED
  ? (mockSignUp as unknown as typeof authClient.signUp)
  : authClient.signUp;
export const signOut = MOCK_ENABLED
  ? (mockSignOut as unknown as typeof authClient.signOut)
  : authClient.signOut;
export const sendVerificationEmail = MOCK_ENABLED
  ? (mockSendVerificationEmail as unknown as typeof authClient.sendVerificationEmail)
  : authClient.sendVerificationEmail;
export const requestPasswordReset = MOCK_ENABLED
  ? (mockRequestPasswordReset as unknown as typeof authClient.requestPasswordReset)
  : authClient.requestPasswordReset;
export const resetPassword = MOCK_ENABLED
  ? (mockResetPassword as unknown as typeof authClient.resetPassword)
  : authClient.resetPassword;
export const organization = MOCK_ENABLED
  ? (mockOrganization as unknown as typeof authClient.organization)
  : authClient.organization;
export const useActiveOrganization = MOCK_ENABLED
  ? (mockUseActiveOrganization as unknown as typeof authClient.useActiveOrganization)
  : authClient.useActiveOrganization;
export const useListOrganizations = MOCK_ENABLED
  ? (mockUseListOrganizations as unknown as typeof authClient.useListOrganizations)
  : authClient.useListOrganizations;
export const apiKey = MOCK_ENABLED
  ? (mockApiKey as unknown as typeof authClient.apiKey)
  : authClient.apiKey;
